@let r = result();

<div class="rv-layout" [class.rv-layout--empty]="!r.snippets.length">
  <!-- Side column on a wide panel; above the list otherwise. -->
  <aside class="rv-rail" aria-label="Summary">
    <!-- Always shown: with no answer it says so and offers questions the
         extractive model handles well. -->
    <section class="rag-answer" [class.rag-answer--open]="answerOpen()" [class.rag-answer--none]="!answer()" aria-label="Most frequent answer">
      <span class="rag-answer__icon" aria-hidden="true">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M3 4.5A1.5 1.5 0 014.5 3h7A1.5 1.5 0 0113 4.5v5a1.5 1.5 0 01-1.5 1.5H7l-3 2.5V11h.5A1.5 1.5 0 013 9.5v-5z" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/></svg>
      </span>
      <div class="rag-answer__body">
        <span class="rag-answer__label rv-key" appFieldTip="meta.most_frequent_answer" [tipText]="tips.mostFrequentAnswer">Most frequent answer</span>
        @if (answer()) {
          <p class="rag-answer__text">@if (answerInPassages()) {<mark class="rag-mark--answer">{{ answer() }}</mark>} @else {<span>{{ answer() }}</span>}</p>
          @if (answerInPassages() && answerOpen()) {
            <span class="rag-answer__hint">Highlighted where it appears in the passages.</span>
          }
        } @else {
          <p class="rag-answer__none">No short answer came back for this question.</p>
        }

        @if (!answer() || answerOpen()) {
          <button type="button" class="rag-answer__more" [class.rag-answer__more--open]="aboutOpen()"
                  [attr.aria-expanded]="aboutOpen()" (click)="aboutOpen.set(!aboutOpen())">
            How is this found?
            <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M6 4l4 4-4 4" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </button>
          @if (aboutOpen()) {
            <div class="rag-answer__about">
              <p class="rag-answer__about-text">
                Returned by a lightweight Transformer-based model fine-tuned for extractive Question Answering. The model
                does not generate new content: it identifies and extracts the most relevant answer span from the
                provided text. It is therefore intended for straightforward, unambiguous questions requiring short,
                factual answers, without any reasoning steps, interpretation, or synthesis.
              </p>
              <button type="button" class="rag-answer__more" [class.rag-answer__more--open]="examplesOpen()"
                      [attr.aria-expanded]="examplesOpen()" (click)="examplesOpen.set(!examplesOpen())">
                {{ examplesOpen() ? 'Hide examples' : 'Show examples' }}
                <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M6 4l4 4-4 4" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>
              </button>
              @if (examplesOpen()) {
                <ul class="rag-answer__examples" aria-label="Example questions">
                  @for (q of answerExamples; track q) {
                    <li>
                      <button type="button" class="rag-answer__example" (click)="exampleQuestion.emit(q)">{{ q }}</button>
                    </li>
                  }
                </ul>
                <span class="rag-answer__hint">Click one to put it in your question.</span>
              }
            </div>
          }
        }
      </div>
      @if (answer()) {
        <button type="button" class="rag-answer__toggle" (click)="answerOpen.set(!answerOpen())"
                [attr.aria-expanded]="answerOpen()" [attr.aria-label]="answerOpen() ? 'Collapse the answer' : 'Expand the answer'">
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M4 6l4 4 4-4" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>
        </button>
      }
    </section>

    @if (r.snippets.length) {
      <app-top-sites [entries]="r.snippets" [selected]="siteFilter()" (toggle)="toggleSite($event)" (clear)="clearSites()" />
    }

    @if (missingFields().length) {
      <p class="rv-tip">
        Turn on <strong>{{ missingFields().join(', ') }}</strong> in Response configuration to see them on each passage.
      </p>
    }
  </aside>

  <div class="rv-main">
    <div class="rv-toolbar">
      <div class="rv-seg" role="tablist" aria-label="Group results">
        <button type="button" role="tab" class="rv-seg__btn" [class.rv-seg__btn--active]="view() === 'passages'"
                [attr.aria-selected]="view() === 'passages'" (click)="setView('passages')">
          Passages <span class="rv-seg__count" appFieldTip="meta.count" [tipText]="tips.count">{{ r.snippets.length }}</span>
        </button>
        <button type="button" role="tab" class="rv-seg__btn" [class.rv-seg__btn--active]="view() === 'sources'"
                [attr.aria-selected]="view() === 'sources'" (click)="setView('sources')">
          Sources <span class="rv-seg__count">{{ sources().length }}</span>
        </button>
      </div>
      @if (siteFilter().size; as picked) {
        @let n = view() === 'passages' ? filteredPassages().length : sources().length;
        <span class="rv-filter" role="status">
          {{ n }} {{ view() === 'passages' ? 'passage' : 'source' }}{{ n === 1 ? '' : 's' }} from
          @if (picked === 1) {
            <strong>{{ siteFilter().values().next().value }}</strong>
          } @else {
            <strong>{{ picked }} sites</strong>
          }
          <button type="button" class="rv-filter__clear" (click)="clearSites()" aria-label="Show results from all sites">
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true"><path d="M3 3l6 6M9 3l-6 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
          </button>
        </span>
      } @else if (r.snippets.length) {
        <span class="rv-toolbar__hint">Best match first</span>
      }
    </div>

    @if (!r.snippets.length) {
      <div class="rv-empty">
        <svg class="rv-empty__icon" width="40" height="40" viewBox="0 0 48 48" fill="none" aria-hidden="true"><circle cx="21" cy="21" r="12" stroke="currentColor" stroke-width="2"/><path d="M30 30l9 9M16 21h10" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
        <p class="rv-empty__title">No passages matched this question.</p>
        <p class="rv-empty__desc">Try a wider date range, fewer filters, or a higher comprehensiveness.</p>
      </div>
    } @else if (view() === 'passages') {
      <ol class="rag-list">
        @for (p of shownPassages(); track p.index) {
          @let thumb = hasImage(p);
          @let open = expanded().has(p.index);
          @let more = open || overflowing().has(p.index);
          <li class="rag-passage">
            <!-- The passage: what was extracted. -->
            <div class="rag-passage__body">
              <span class="rag-passage__quote" aria-hidden="true">“</span>

              <!-- One line on purpose: whitespace between the parts would add stray spaces around highlighted words. -->
              @if (showingOriginal().has(p.index)) {
                <p class="rag-passage__text" [class.rag-passage__text--clamped]="!open" (appClampOverflow)="setOverflowing(p.index, $event)"
                   appFieldTip="original_language_snippet" [tipText]="tips.originalLanguageSnippet" [tipDelay]="900">{{ p.snippet.originalLanguageSnippet }}</p>
              } @else {
                <p class="rag-passage__text" [class.rag-passage__text--clamped]="!open" (appClampOverflow)="setOverflowing(p.index, $event)" lang="en" appFieldTip="snippet" [tipText]="tips.snippet" [tipDelay]="900">@for (part of p.parts; track $index) {@if (part.mark) {<mark [class.rag-mark--answer]="part.mark === 'answer'">{{ part.text }}</mark>} @else {<span>{{ part.text }}</span>}}</p>
              }

              @if (more || p.snippet.originalLanguageSnippet) {
                <div class="rag-passage__actions">
                  @if (more) {
                    <button type="button" class="rag-passage__action" (click)="toggleExpanded(p.index)" [attr.aria-expanded]="open">
                      {{ open ? 'Show less' : 'Show more' }}
                    </button>
                  }
                  @if (p.snippet.originalLanguageSnippet) {
                    <button type="button" class="rag-passage__action rag-passage__action--lang" (click)="toggleOriginal(p.index)"
                            appFieldTip="original_language_snippet" [tipText]="tips.originalLanguageSnippet">
                      <svg width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M2 3.5h7M5.5 2v1.5M4 3.5c.5 2.5 2 4.5 4.5 5.5M7.5 3.5C7 6 5.2 8.2 2.5 9.5M8.5 14l2.75-6.5L14 14M9.4 12h3.7" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/></svg>
                      {{ showingOriginal().has(p.index) ? 'Show English' : 'Show original language' }}
                    </button>
                  }
                </div>
              }
            </div>

            <!-- Where it came from: the article, as attribution. -->
            <div class="rag-passage__source">
              @if (thumb) {
                <a class="rag-passage__thumb" [href]="p.snippet.url" target="_blank" rel="noopener noreferrer" tabindex="-1" aria-hidden="true"
                   appFieldTip="image_url" [tipText]="tips.imageUrl" [tipDelay]="500">
                  <img [src]="p.snippet.imageUrl" alt="" loading="lazy" (error)="imageFailed(p.index)" />
                </a>
              }
              <div class="rag-passage__origin">
                <div class="rag-passage__meta">
                  <a class="rag-passage__link" [href]="p.snippet.url" target="_blank" rel="noopener noreferrer"
                     [appFieldTip]="p.snippet.domainName ? 'domain_name' : 'url'"
                     [tipText]="(p.snippet.domainName ? tips.domainName : tips.url) + ' ' + p.snippet.url">
                    @if (p.snippet.favicon) {
                      <img class="rv-favicon" [src]="p.snippet.favicon" alt="" width="14" height="14" loading="lazy" (error)="hideBrokenImage($event)" />
                    }
                    <span class="rag-passage__host">{{ p.snippet.host }}</span>
                    <svg width="11" height="11" viewBox="0 0 12 12" fill="none" aria-hidden="true"><path d="M4.5 2.5h5v5M9.5 2.5L3 9" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>
                    <span class="sr-only">(opens the original article)</span>
                  </a>
                  @if (p.snippet.publishedAt) {
                    <span class="rag-passage__dot" aria-hidden="true">·</span>
                    <time class="rv-key" [attr.datetime]="p.snippet.publishedAt" appFieldTip="published_at" [tipText]="tips.publishedAt">{{ p.snippet.publishedAt | date:'mediumDate' }}</time>
                  }
                </div>
                @if (p.snippet.articleTitle) {
                  <a class="rag-passage__title" [href]="p.snippet.url" target="_blank" rel="noopener noreferrer"
                     appFieldTip="article_title" [tipText]="tips.articleTitle" [tipDelay]="500">{{ p.snippet.articleTitle }}</a>
                }
              </div>
              @if (p.snippet.relevanceScore !== null) {
                <span class="rv-score" tabindex="0" appFieldTip="relevance_score" [tipText]="tips.relevanceScore"><span class="rv-score__dot"></span>{{ p.snippet.relevanceScore | number:'1.2-2' }}</span>
              }
            </div>
          </li>
        }
      </ol>

      @if (filteredPassages().length > shownPassages().length) {
        <button type="button" class="btn--secondary rv-more" (click)="showMore()">
          Show more ({{ filteredPassages().length - shownPassages().length }} left)
        </button>
      }
    } @else {
      <ul class="rag-sources">
        @for (s of sources(); track s.url) {
          <li class="rag-source">
            @if (s.favicon) {
              <img class="rv-favicon" [src]="s.favicon" alt="" width="14" height="14" loading="lazy" (error)="hideBrokenImage($event)" />
            }
            <div class="rag-source__text">
              <a class="rag-source__title" [href]="s.url" target="_blank" rel="noopener noreferrer">{{ s.title }}</a>
              <span class="rag-source__meta">
                {{ s.host }}
                @if (s.publishedAt) { · {{ s.publishedAt | date:'mediumDate' }} }
              </span>
            </div>
            <span class="rag-source__count">{{ s.passages }} passage{{ s.passages === 1 ? '' : 's' }}</span>
          </li>
        }
      </ul>
    }
  </div>
</div>
